import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
    Loader2,
    Users,
    Landmark,
    Wrench,
    ArrowLeft,
    Check,
    AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
    adminListPartnerLeads,
    adminRoutePartnerLead,
    adminUpdatePartnerLeadStatus,
} from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";

const LEAD_TYPES = [
    { value: "", label: "All" },
    { value: "quote", label: "Quote requests" },
    { value: "lender_estimate", label: "Lender estimates" },
];

const STATUS_FILTERS = [
    { value: "", label: "Any" },
    { value: "submitted", label: "Submitted" },
    { value: "new", label: "New" },
    { value: "reviewing", label: "Reviewing" },
    { value: "routed", label: "Routed" },
    { value: "contacted", label: "Contacted" },
    { value: "won", label: "Won" },
    { value: "lost", label: "Lost" },
    { value: "closed", label: "Closed" },
];

function MiniStat({ label, value, testid }) {
    return (
        <div className="bl-card p-4" data-testid={testid}>
            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                {label}
            </div>
            <div className="mt-1.5 font-display font-semibold text-2xl text-bl tnum">
                {value ?? "—"}
            </div>
        </div>
    );
}

function LeadIcon({ type }) {
    if (type === "lender_estimate")
        return <Landmark className="h-4 w-4 text-bl-primary" />;
    return <Wrench className="h-4 w-4 text-bl-primary" />;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString();
    } catch {
        return iso.slice(0, 10);
    }
}

function statusClass(status) {
    if (status === "won") return "text-bl-primary font-semibold";
    if (status === "lost" || status === "cancelled") return "text-bl-muted";
    if (status === "routed" || status === "contacted") return "text-bl";
    return "text-bl-muted";
}

export default function AdminPartnerLeads() {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [leadType, setLeadType] = useState("");
    const [status, setStatus] = useState("");
    const [unroutedOnly, setUnroutedOnly] = useState(false);
    const [routeOpen, setRouteOpen] = useState(null); // lead row being routed
    const [routedTo, setRoutedTo] = useState("");
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const r = await adminListPartnerLeads({
                lead_type: leadType || undefined,
                status: status || undefined,
                unrouted_only: unroutedOnly ? "true" : undefined,
                window_days: 60,
                limit: 200,
            });
            setData(r);
        } catch (e) {
            toast.error("Could not load partner leads.");
        } finally {
            setLoading(false);
        }
    }, [leadType, status, unroutedOnly]);

    useEffect(() => {
        load();
    }, [load]);

    const onRoute = async () => {
        if (!routeOpen) return;
        if (!routedTo.trim()) {
            toast.error("Enter a partner name to route to.");
            return;
        }
        setBusy(true);
        try {
            await adminRoutePartnerLead(routeOpen.lead_type, routeOpen.lead_id, {
                routed_to: routedTo.trim(),
            });
            toast.success(`Routed to ${routedTo.trim()}.`);
            setRouteOpen(null);
            setRoutedTo("");
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not route this lead.",
            );
        } finally {
            setBusy(false);
        }
    };

    const onStatusChange = async (lead, newStatus) => {
        try {
            await adminUpdatePartnerLeadStatus(lead.lead_type, lead.lead_id, {
                status: newStatus,
            });
            toast.success(`Status updated to ${newStatus}.`);
            await load();
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "Could not update status.",
            );
        }
    };

    return (
        <PublicLayout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="admin-partner-leads">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="bl-eyebrow">ADMIN</div>
                    <h1 className="font-display font-semibold text-2xl text-bl tracking-tight mt-1">
                        Partner Leads
                    </h1>
                    <p className="text-sm text-bl-muted mt-1">
                        Unified inbox for quote requests + lender estimates.
                        Route to partners and track status from one place.
                    </p>
                </div>
                <Link
                    to="/admin"
                    className="text-sm text-bl-muted hover:text-bl flex items-center gap-1.5"
                    data-testid="back-to-admin"
                >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Back to admin
                </Link>
            </div>

            {data && (
                <div
                    className="grid grid-cols-2 sm:grid-cols-4 gap-3"
                    data-testid="partner-leads-counters"
                >
                    <MiniStat
                        label="Quote requests"
                        value={data.counters.quote_total}
                        testid="cnt-quote"
                    />
                    <MiniStat
                        label="Lender estimates"
                        value={data.counters.lender_total}
                        testid="cnt-lender"
                    />
                    <MiniStat
                        label="Unrouted total"
                        value={data.counters.unrouted_total}
                        testid="cnt-unrouted"
                    />
                    <MiniStat
                        label="Window"
                        value={`${data.window_days}d`}
                        testid="cnt-window"
                    />
                </div>
            )}

            <div className="bl-card p-4 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                    <span className="text-xs text-bl-muted uppercase tracking-wide">
                        Type
                    </span>
                    <select
                        value={leadType}
                        onChange={(e) => setLeadType(e.target.value)}
                        className="bl-input"
                        data-testid="filter-lead-type"
                    >
                        {LEAD_TYPES.map((t) => (
                            <option key={t.value || "all"} value={t.value}>
                                {t.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs text-bl-muted uppercase tracking-wide">
                        Status
                    </span>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="bl-input"
                        data-testid="filter-status"
                    >
                        {STATUS_FILTERS.map((s) => (
                            <option key={s.value || "any"} value={s.value}>
                                {s.label}
                            </option>
                        ))}
                    </select>
                </div>
                <label className="flex items-center gap-2 text-sm text-bl-muted">
                    <input
                        type="checkbox"
                        checked={unroutedOnly}
                        onChange={(e) => setUnroutedOnly(e.target.checked)}
                        data-testid="filter-unrouted"
                    />
                    Unrouted only
                </label>
            </div>

            <div className="bl-card overflow-x-auto" data-testid="partner-leads-table">
                {loading ? (
                    <div className="flex items-center gap-2 text-sm text-bl-muted p-6">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                    </div>
                ) : !data || data.leads.length === 0 ? (
                    <div className="p-8 text-center text-bl-muted text-sm">
                        No leads matching the current filters.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left">
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Type
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Name
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Contact
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    ZIP
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Summary
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Status
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Routed to
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl">
                                    Created
                                </th>
                                <th className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-bl-muted font-medium border-b border-bl text-right">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.leads.map((lead) => (
                                <tr
                                    key={`${lead.lead_type}-${lead.lead_id}`}
                                    className="border-b border-bl/30 last:border-0"
                                    data-testid={`lead-row-${lead.lead_id}`}
                                >
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-1.5">
                                            <LeadIcon type={lead.lead_type} />
                                            <span className="text-xs text-bl-muted uppercase tracking-wide">
                                                {lead.lead_type === "quote"
                                                    ? "Quote"
                                                    : "Lender"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-bl">
                                        {lead.name || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-bl-muted">
                                        <div>{lead.email || "—"}</div>
                                        {lead.phone && (
                                            <div className="text-bl-muted">
                                                {lead.phone}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-bl-muted font-mono">
                                        {lead.zip_code || "—"}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-bl-muted">
                                        {lead.summary || "—"}
                                    </td>
                                    <td className="px-4 py-3">
                                        <select
                                            value={lead.status}
                                            onChange={(e) =>
                                                onStatusChange(lead, e.target.value)
                                            }
                                            className={`bl-input text-xs ${statusClass(lead.status)}`}
                                            data-testid={`status-select-${lead.lead_id}`}
                                        >
                                            {STATUS_FILTERS.filter(
                                                (s) => s.value,
                                            ).map((s) => (
                                                <option key={s.value} value={s.value}>
                                                    {s.label}
                                                </option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {lead.routed_to ? (
                                            <span className="text-bl-primary flex items-center gap-1">
                                                <Check className="h-3 w-3" />
                                                {lead.routed_to}
                                            </span>
                                        ) : (
                                            <span className="text-bl-muted flex items-center gap-1">
                                                <AlertCircle className="h-3 w-3" />
                                                Not routed
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-bl-muted">
                                        {fmtDate(lead.created_at)}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        {!lead.routed_to_partner ? (
                                            <button
                                                onClick={() =>
                                                    setRouteOpen(lead)
                                                }
                                                className="text-xs bl-btn-primary px-3 py-1.5"
                                                data-testid={`route-btn-${lead.lead_id}`}
                                            >
                                                Route to partner
                                            </button>
                                        ) : (
                                            <span className="text-xs text-bl-muted">
                                                Routed
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {routeOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
                    onClick={() => !busy && setRouteOpen(null)}
                    data-testid="route-modal-overlay"
                >
                    <div
                        className="bl-card w-full max-w-md bg-white p-6"
                        onClick={(e) => e.stopPropagation()}
                        data-testid="route-modal"
                    >
                        <div className="flex items-center gap-2 mb-4">
                            <Users className="h-5 w-5 text-bl-primary" />
                            <h3 className="font-display font-semibold text-base text-bl">
                                Route to partner
                            </h3>
                        </div>
                        <p className="text-sm text-bl-muted mb-4">
                            <span className="text-bl">{routeOpen.name || "—"}</span>{" "}
                            · {routeOpen.summary || "—"} · ZIP{" "}
                            {routeOpen.zip_code || "—"}
                        </p>
                        <label className="block text-xs uppercase tracking-wide text-bl-muted mb-1.5">
                            Partner name
                        </label>
                        <input
                            type="text"
                            value={routedTo}
                            onChange={(e) => setRoutedTo(e.target.value)}
                            placeholder="e.g. Modernize · ZIP 28801"
                            className="bl-input w-full"
                            data-testid="route-modal-input"
                            autoFocus
                        />
                        <div className="flex items-center justify-end gap-2 mt-4">
                            <button
                                onClick={() => setRouteOpen(null)}
                                disabled={busy}
                                className="text-sm px-4 py-2 text-bl-muted hover:text-bl"
                                data-testid="route-modal-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onRoute}
                                disabled={busy || !routedTo.trim()}
                                className="bl-btn-primary text-sm px-4 py-2 disabled:opacity-50"
                                data-testid="route-modal-confirm"
                            >
                                {busy ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    "Confirm route"
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            </div>
        </PublicLayout>
    );
}
